import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, FileSpreadsheet, AlertTriangle } from "lucide-react";

const FLAG_COLOR = {
  normal:   "#4ADE80",
  low:      "#38BDF8",
  high:     "#FBBF24",
  abnormal: "#F87171",
  critical: "#EC4899",
};

/**
 * ResultFilingModal — attach results to an existing diagnostic order.
 *
 * The order has a list of tests (each with code + name). This modal renders a
 * one-row-per-test grid: value, unit, flag (normal / low / high / abnormal /
 * critical). On submit, PATCH /encounters/orders/{id}/result — which
 * automatically fires a cross-specialty critical alert if any flag is abnormal.
 *
 * Abnormal rows are visually escalated in the summary strip so nothing is
 * missed.
 */
export default function ResultFilingModal({ order, onClose, onSaved }) {
  const [rows, setRows] = useState(() =>
    (order?.tests || []).map((t) => ({
      code: t.code, name: t.name, value: "", unit: "", flag: "normal",
    }))
  );
  const [resultNotes, setResultNotes] = useState("");
  const [interpretation, setInterpretation] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (order?.results && order.results.length > 0) {
      // Pre-populate if editing an already-filed set
      setRows(order.results);
      setResultNotes(order.result_notes || "");
      setInterpretation(order.interpretation || "");
    }
  }, [order]);

  const abnormalCount = useMemo(
    () => rows.filter((r) => ["low", "high", "abnormal", "critical"].includes(r.flag)).length,
    [rows]
  );

  function setRow(i, patch) {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  async function save() {
    if (rows.some((r) => !String(r.value ?? "").trim())) {
      toast.error("Fill a value for every test (use N/A if not run)");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/encounters/orders/${order.id}/result`, {
        results: rows,
        result_notes: resultNotes,
        interpretation,
      });
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to file results");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
         onClick={onClose} data-testid="result-modal">
      <div className="bg-[#0F1312] border border-[#272E2B] rounded-3xl w-full max-w-4xl max-h-[92vh] overflow-y-auto p-6 space-y-4"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading text-xl flex items-center gap-2">
              <FileSpreadsheet size={18} /> File diagnostic results
            </div>
            <div className="text-xs text-[#A3ACA7] mt-0.5">
              Order {order.id} · {(order.tests || []).length} tests · urgency {order.urgency}
            </div>
          </div>
          <button onClick={onClose} data-testid="result-close"
                  className="text-[#A3ACA7] hover:text-white text-xl leading-none">✕</button>
        </div>

        {/* Live abnormal counter */}
        {abnormalCount > 0 && (
          <div className="ros-card flex items-center gap-2"
               style={{ borderColor: "#F87171", background: "rgba(248,113,113,0.08)" }}
               data-testid="result-abnormal-strip">
            <AlertTriangle size={16} className="text-[#F87171] shrink-0" />
            <div className="text-xs text-white">
              <strong className="text-[#F87171]">{abnormalCount} abnormal result{abnormalCount === 1 ? "" : "s"}</strong>
              {" "}— a critical alert will fire to the ordering doctor on save.
            </div>
          </div>
        )}

        {/* Results table */}
        <div className="ros-card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#A3ACA7] text-left">
                <th className="py-2 px-3">Test</th>
                <th className="py-2 px-3">Value</th>
                <th className="py-2 px-3">Unit</th>
                <th className="py-2 px-3">Flag</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.code}-${i}`} className="border-t border-[#272E2B]"
                    data-testid={`result-row-${r.code.replace(/[^a-zA-Z0-9]/g, "-")}`}>
                  <td className="py-2 px-3 align-top">
                    <div className="text-white">{r.name}</div>
                    <div className="text-[10px] text-[#7A8480]">{r.code}</div>
                  </td>
                  <td className="py-2 px-3 align-top">
                    <input value={r.value}
                           onChange={(e) => setRow(i, { value: e.target.value })}
                           data-testid={`result-value-${i}`}
                           className="ros-input w-full min-w-[110px]"
                           placeholder="e.g. 12.4 or 'Positive'" />
                  </td>
                  <td className="py-2 px-3 align-top">
                    <input value={r.unit}
                           onChange={(e) => setRow(i, { unit: e.target.value })}
                           data-testid={`result-unit-${i}`}
                           className="ros-input w-full min-w-[80px]"
                           placeholder="g/dL, mg/L…" />
                  </td>
                  <td className="py-2 px-3 align-top">
                    <select value={r.flag}
                            onChange={(e) => setRow(i, { flag: e.target.value })}
                            data-testid={`result-flag-${i}`}
                            className="ros-input"
                            style={{
                              color: FLAG_COLOR[r.flag] || "#A3ACA7",
                              borderColor: FLAG_COLOR[r.flag] || "#272E2B",
                            }}>
                      <option value="normal">Normal</option>
                      <option value="low">Low</option>
                      <option value="high">High</option>
                      <option value="abnormal">Abnormal</option>
                      <option value="critical">Critical</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Result notes (lab)</span>
          <textarea rows={2} value={resultNotes}
                    onChange={(e) => setResultNotes(e.target.value)}
                    data-testid="result-notes"
                    className="ros-input" placeholder="Lab comments, artefacts, technique…" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">
            Clinical interpretation (for the care team)
          </span>
          <textarea rows={3} value={interpretation}
                    onChange={(e) => setInterpretation(e.target.value)}
                    data-testid="result-interpretation"
                    className="ros-input"
                    placeholder="Findings, likely diagnoses, next steps" />
        </label>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#272E2B]">
          <button onClick={onClose} data-testid="result-cancel"
                  className="text-sm px-4 py-2 rounded-full bg-[#1C201E] text-white">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
                  data-testid="result-save"
                  className="flex items-center gap-1.5 text-sm px-5 py-2 rounded-full font-semibold text-black disabled:opacity-50"
                  style={{ background: abnormalCount > 0 ? "#F87171" : "#4ADE80" }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            File results{abnormalCount > 0 ? ` · alert on ${abnormalCount} abnormal` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
